/**
 * Agent-detail `?tab=` vocabulary (CLA-1007).
 *
 * Deliberately dependency-free: the eager `$agentId.tsx` route file imports the
 * guard for its `validateSearch`, and every eager route file is bundled into the
 * always-loaded `main` entry chunk. Importing it from the atoms module would drag
 * the whole Effect/RPC/design graph of `lib/atoms/customer-agent.ts` into `main`
 * (measured: 30.7 KiB -> 64.0 KiB gzip, double the 32 KiB budget). The atoms
 * module re-exports these names so page code keeps one import site.
 *
 * @since 1.0.0
 */
/**
 * The agent-detail tab vocabulary carried in `?tab=`. It is the design
 * component's `AgentTab` union MINUS its link-out `"configure"` (Configure is a
 * sibling route, reached by navigation, never a tab body here). The tab lives in
 * the URL — not a module atom — so deep-linking a tab renders it and switching
 * agents never carries the previous agent's tab.
 *
 * @since 1.0.0
 */
export type AgentDetailTab = "overview" | "access" | "activity" | "billing";

/**
 * The tab ids in display order.
 *
 * @since 1.0.0
 */
export const AGENT_DETAIL_TABS: ReadonlyArray<AgentDetailTab> = ["overview", "access", "activity", "billing"];

/**
 * Narrow an unknown `?tab=` value to a known {@link AgentDetailTab}. Backs the
 * route's `validateSearch`; an unknown value falls back cleanly (the page then
 * defaults to Overview).
 *
 * @since 1.0.0
 */
export const isAgentDetailTab = (value: unknown): value is AgentDetailTab =>
  value === "overview" || value === "access" || value === "activity" || value === "billing";
